import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { hiltiWorkersAPI } from '../services/api';
import { HiltiWorker } from '../types';
import { showCustomAlert } from './CustomAlert';

const HiltiWorkers: React.FC = () => {
  const [workers, setWorkers] = useState<HiltiWorker[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(false);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<HiltiWorker | null>(null);
  const [formData, setFormData] = useState({ firstName: '', lastName: '' });
  const [error, setError] = useState('');

  const dialogHeader = useMemo(
    () => (editing ? 'Izmeni radnicu' : 'Dodaj radnicu'),
    [editing]
  );

  const loadWorkers = async () => {
    setLoading(true);
    try {
      const data = await hiltiWorkersAPI.getAll({ showInactive });
      setWorkers(data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju Hilti radnica';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const openCreate = () => {
    setEditing(null);
    setFormData({ firstName: '', lastName: '' });
    setError('');
    setDialogVisible(true);
  };

  const openEdit = (worker: HiltiWorker) => {
    setEditing(worker);
    setFormData({ firstName: worker.firstName, lastName: worker.lastName });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Ime i prezime su obavezni');
      return;
    }
    try {
      setError('');
      if (editing?._id) {
        await hiltiWorkersAPI.update(editing._id, {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim()
        });
        showCustomAlert.success('Radnica je ažurirana');
      } else {
        await hiltiWorkersAPI.create({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim()
        });
        showCustomAlert.success('Radnica je dodata');
      }
      setDialogVisible(false);
      await loadWorkers();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju Hilti radnice';
      setError(msg);
      showCustomAlert.error(msg);
    }
  };

  const toggleActive = (worker: HiltiWorker) => {
    const action = worker.isActive ? 'deactivate' : 'reactivate';
    const message = worker.isActive
      ? `Da li ste sigurni da želite da deaktivirate radnicu ${worker.firstName} ${worker.lastName}?`
      : `Da li ste sigurni da želite da aktivirate radnicu ${worker.firstName} ${worker.lastName}?`;

    confirmDialog({
      header: worker.isActive ? 'Deaktivacija' : 'Aktivacija',
      icon: 'pi pi-exclamation-triangle',
      message,
      acceptLabel: worker.isActive ? 'Deaktiviraj' : 'Aktiviraj',
      rejectLabel: 'Otkaži',
      acceptClassName: worker.isActive ? 'p-button-danger' : undefined,
      accept: async () => {
        try {
          if (action === 'deactivate') {
            await hiltiWorkersAPI.deactivate(worker._id);
          } else {
            await hiltiWorkersAPI.reactivate(worker._id);
          }
          await loadWorkers();
        } catch (err: any) {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            'Greška pri promeni statusa radnice';
          showCustomAlert.error(msg);
        }
      }
    });
  };

  const deleteWorker = (worker: HiltiWorker) => {
    confirmDialog({
      header: 'Trajno brisanje',
      icon: 'pi pi-exclamation-triangle',
      message: `Da li ste sigurni da želite da trajno obrišete radnicu ${worker.firstName} ${worker.lastName}? Ova akcija se ne može poništiti.`,
      acceptLabel: 'Obriši trajno',
      rejectLabel: 'Otkaži',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await hiltiWorkersAPI.delete(worker._id);
          await loadWorkers();
        } catch (err: any) {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            'Greška pri trajnom brisanju radnice';
          showCustomAlert.error(msg);
        }
      }
    });
  };

  const statusTemplate = (w: HiltiWorker) => {
    const label = w.isActive ? 'Aktivna' : 'Neaktivna';
    const cls = w.isActive ? 'bg-green-500' : 'bg-gray-500';
    return (
      <span className={`inline-flex align-items-center gap-2 px-3 py-1 text-white text-sm border-round ${cls}`}>
        <i className={`pi ${w.isActive ? 'pi-check' : 'pi-times'}`} />
        {label}
      </span>
    );
  };

  const actionsTemplate = (w: HiltiWorker) => (
    <div className="flex gap-2 justify-content-end">
      <Button
        icon="pi pi-pencil"
        className="action-icon-btn"
        text
        rounded
        onClick={() => openEdit(w)}
        tooltip="Izmeni"
      />
      <Button
        icon={w.isActive ? 'pi pi-times' : 'pi pi-check'}
        className="action-icon-btn"
        text
        rounded
        severity={w.isActive ? 'warning' : 'success'}
        onClick={() => toggleActive(w)}
        tooltip={w.isActive ? 'Deaktiviraj' : 'Aktiviraj'}
      />
      <Button
        icon="pi pi-trash"
        className="action-icon-btn"
        text
        rounded
        severity="danger"
        onClick={() => deleteWorker(w)}
        tooltip="Obriši trajno"
      />
    </div>
  );

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Hilti Radnice" subTitle="Lista radnica">
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="flex justify-content-between align-items-center" style={{ marginBottom: '1rem' }}>
          <Button label="Dodaj radnicu" icon="pi pi-plus" onClick={openCreate} />
          <Button
            label={showInactive ? 'Prikaži samo aktivne' : 'Prikaži i neaktivne'}
            icon={showInactive ? 'pi pi-eye-slash' : 'pi pi-eye'}
            outlined
            onClick={() => setShowInactive(prev => !prev)}
          />
        </div>

        <DataTable
          value={workers}
          loading={loading}
          paginator
          rows={15}
          rowsPerPageOptions={[15, 30, 50]}
          responsiveLayout="scroll"
        >
          <Column field="lastName" header="Prezime" sortable />
          <Column field="firstName" header="Ime" sortable />
          <Column header="Status" body={statusTemplate} style={{ width: '160px' }} />
          <Column header="Akcije" body={actionsTemplate} style={{ width: '220px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={dialogHeader}
        visible={dialogVisible}
        style={{ width: '520px', maxWidth: '92vw' }}
        onHide={() => setDialogVisible(false)}
        modal
        closable
      >
        <div className="p-fluid">
          {error && (
            <div style={{ marginBottom: '1rem' }}>
              <Message severity="error" text={error} />
            </div>
          )}
          <div className="field">
            <label htmlFor="firstName" style={{ fontWeight: 600 }}>
              Ime*
            </label>
            <InputText
              id="firstName"
              value={formData.firstName}
              onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
            />
          </div>
          <div className="field" style={{ marginTop: '0.75rem' }}>
            <label htmlFor="lastName" style={{ fontWeight: 600 }}>
              Prezime*
            </label>
            <InputText
              id="lastName"
              value={formData.lastName}
              onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
            />
          </div>

          <div className="flex gap-2 justify-content-end" style={{ marginTop: '1.25rem' }}>
            <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} />
            <Button
              label="Otkaži"
              icon="pi pi-times"
              severity="secondary"
              outlined
              onClick={() => setDialogVisible(false)}
            />
          </div>
        </div>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default HiltiWorkers;


